import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Loader2, Upload, FileText, AlertTriangle, X,
  Leaf, ArrowLeftRight, Pill, ShoppingCart, Ruler,
  BookOpen, Heart, Apple, Dumbbell, Coffee, Salad,
} from "lucide-react";
import { apiFetch } from "@/lib/api";
import { toast } from "sonner";

const ICON_MAP = {
  FileText, Leaf, ArrowLeftRight, Pill, ShoppingCart, Ruler,
  BookOpen, Heart, Apple, Dumbbell, Coffee, Salad,
} as const;

export const ICON_OPTIONS = Object.keys(ICON_MAP);

export interface MaterialFile {
  id: string;
  file_path: string;
  original_name: string;
  size_bytes: number;
  sort_order: number;
}

export interface MaterialRecord {
  id: string;
  title: string;
  description: string;
  icon: string;
  file_path: string;
  required_plan: string;
  sort_order: number;
  is_active: boolean;
  files: MaterialFile[];
}

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  material?: MaterialRecord | null;
  onSaved: () => void;
}

const MAX_MB = 25;
const MAX_FILES = 20;

export function MaterialFormDialog({ open, onOpenChange, material, onSaved }: Props) {
  const isEdit = !!material;
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [icon, setIcon] = useState("FileText");
  const [requiredPlan, setRequiredPlan] = useState("complete");
  const [sortOrder, setSortOrder] = useState(0);
  const [isActive, setIsActive] = useState(true);
  const [existingFiles, setExistingFiles] = useState<MaterialFile[]>([]);
  const [removedExistingIds, setRemovedExistingIds] = useState<string[]>([]);
  const [newPdfs, setNewPdfs] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setTitle(material?.title || "");
      setDescription(material?.description || "");
      setIcon(material?.icon || "FileText");
      setRequiredPlan(material?.required_plan || "complete");
      setSortOrder(material?.sort_order ?? 0);
      setIsActive(material?.is_active ?? true);
      setExistingFiles(material?.files || []);
      setRemovedExistingIds([]);
      setNewPdfs([]);
    }
  }, [open, material]);

  const remainingExisting = existingFiles.filter((f) => !removedExistingIds.includes(f.id));
  const totalCount = remainingExisting.length + newPdfs.length;

  const handleAddFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const accepted: File[] = [];
    for (const f of Array.from(files)) {
      if (f.type !== "application/pdf") {
        toast.error(`"${f.name}" não é um PDF`);
        continue;
      }
      if (f.size > MAX_MB * 1024 * 1024) {
        toast.error(`"${f.name}" excede ${MAX_MB} MB`);
        continue;
      }
      accepted.push(f);
    }
    const projected = totalCount + accepted.length;
    if (projected > MAX_FILES) {
      toast.error(`Máximo de ${MAX_FILES} arquivos por material`);
      return;
    }
    setNewPdfs((prev) => [...prev, ...accepted]);
  };

  const removeExisting = (id: string) => {
    setRemovedExistingIds((prev) => [...prev, id]);
  };

  const removeNew = (idx: number) => {
    setNewPdfs((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      toast.error("Informe o título");
      return;
    }
    if (totalCount === 0) {
      toast.error("Anexe ao menos 1 arquivo PDF");
      return;
    }
    if (totalCount > MAX_FILES) {
      toast.error(`Máximo de ${MAX_FILES} arquivos por material`);
      return;
    }

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("title", title.trim());
      fd.append("description", description);
      fd.append("icon", icon);
      fd.append("required_plan", requiredPlan);
      fd.append("sort_order", String(sortOrder));
      if (isEdit) {
        fd.append("id", material!.id);
        fd.append("is_active", String(isActive));
        if (removedExistingIds.length > 0) {
          fd.append("delete_file_ids", JSON.stringify(removedExistingIds));
        }
      }
      for (const f of newPdfs) {
        fd.append("pdfs", f, f.name);
      }

      await apiFetch("api-materials", { action: isEdit ? "update" : "create" }, {
        method: "POST",
        formData: fd,
      });

      toast.success(isEdit ? "Material atualizado" : "Material criado");
      onSaved();
      onOpenChange(false);
    } catch (e: any) {
      toast.error(e.message || "Erro ao salvar");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto overflow-x-hidden">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Editar material" : "Novo material"}</DialogTitle>
          <DialogDescription>
            Preencha as informações e envie até {MAX_FILES} PDFs por material.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="m-title">Título *</Label>
            <Input id="m-title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="m-desc">Descrição</Label>
            <Textarea id="m-desc" value={description} onChange={(e) => setDescription(e.target.value)} rows={3} maxLength={1000} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Ícone</Label>
              <Select value={icon} onValueChange={setIcon}>
                <SelectTrigger>
                  <SelectValue>
                    {(() => {
                      const Ico = ICON_MAP[icon as keyof typeof ICON_MAP] || FileText;
                      return (
                        <span className="flex items-center gap-2">
                          <Ico className="w-4 h-4" />
                          {icon}
                        </span>
                      );
                    })()}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {ICON_OPTIONS.map((i) => {
                    const Ico = ICON_MAP[i as keyof typeof ICON_MAP];
                    return (
                      <SelectItem key={i} value={i}>
                        <span className="flex items-center gap-2">
                          <Ico className="w-4 h-4" />
                          {i}
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label>Plano mínimo</Label>
              <Select value={requiredPlan} onValueChange={setRequiredPlan}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="basic">Basic</SelectItem>
                  <SelectItem value="plus">Plus</SelectItem>
                  <SelectItem value="complete">Max</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 items-end">
            <div className="space-y-1.5">
              <Label htmlFor="m-order">Ordem</Label>
              <Input
                id="m-order"
                type="number"
                value={sortOrder}
                onChange={(e) => setSortOrder(parseInt(e.target.value) || 0)}
              />
            </div>
            {isEdit && (
              <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
                <Label htmlFor="m-active" className="cursor-pointer">Ativo</Label>
                <Switch id="m-active" checked={isActive} onCheckedChange={setIsActive} />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Arquivos PDF *</Label>
              <span className="text-xs text-muted-foreground">{totalCount} / {MAX_FILES}</span>
            </div>

            {(remainingExisting.length > 0 || newPdfs.length > 0) && (
              <ul className="space-y-1.5 w-full">
                {remainingExisting.map((f) => (
                  <li key={f.id} className="flex items-center gap-2 rounded-md border border-border bg-muted/30 px-2.5 py-1.5 w-full min-w-0">
                    <FileText className="w-4 h-4 text-primary shrink-0" />
                    <span className="text-sm text-foreground truncate flex-1 min-w-0" title={f.original_name}>
                      {f.original_name}
                    </span>
                    {f.size_bytes > 0 && (
                      <span className="text-xs text-muted-foreground shrink-0">
                        {(f.size_bytes / (1024 * 1024)).toFixed(2)} MB
                      </span>
                    )}
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
                      onClick={() => removeExisting(f.id)}
                      title="Remover"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </li>
                ))}
                {newPdfs.map((f, idx) => (
                  <li key={`new-${idx}`} className="flex items-center gap-2 rounded-md border border-dashed border-primary/40 bg-primary/5 px-2.5 py-1.5 w-full min-w-0">
                    <Upload className="w-4 h-4 text-primary shrink-0" />
                    <span className="text-sm text-foreground truncate flex-1 min-w-0" title={f.name}>
                      {f.name}
                    </span>
                    <span className="text-xs text-muted-foreground shrink-0">
                      {(f.size / (1024 * 1024)).toFixed(2)} MB
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
                      onClick={() => removeNew(idx)}
                      title="Remover"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}

            <label
              htmlFor="m-pdfs"
              className={`flex items-center justify-center gap-2 rounded-lg border border-dashed border-border px-3 py-3 text-sm cursor-pointer hover:bg-muted/40 transition ${
                totalCount >= MAX_FILES ? "opacity-50 pointer-events-none" : ""
              }`}
            >
              <Upload className="w-4 h-4 text-muted-foreground" />
              <span className="text-muted-foreground">
                {totalCount === 0 ? "Selecionar PDFs" : "Adicionar mais PDFs"}
              </span>
              <input
                id="m-pdfs"
                type="file"
                accept="application/pdf"
                multiple
                className="hidden"
                onChange={(e) => {
                  handleAddFiles(e.target.files);
                  e.target.value = "";
                }}
                disabled={totalCount >= MAX_FILES}
              />
            </label>

            <div className="flex items-start gap-2 rounded-md bg-amber-500/10 border border-amber-500/20 p-2.5">
              <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
              <p className="text-xs text-amber-700 dark:text-amber-300 leading-snug">
                Compacte os PDFs antes de enviar (use{" "}
                <a href="https://www.ilovepdf.com/pt/comprimir_pdf" target="_blank" rel="noopener noreferrer" className="underline">ilovepdf.com</a>{" "}
                ou{" "}
                <a href="https://smallpdf.com/pt/comprimir-pdf" target="_blank" rel="noopener noreferrer" className="underline">smallpdf.com</a>).
                Tamanho máximo por arquivo: {MAX_MB} MB. Máximo de {MAX_FILES} arquivos por material.
              </p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {isEdit ? "Salvar" : "Criar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
